<template>
  <main class="wallet-page afro-bg">
    <section class="wallet-shell">
      <header class="wallet-info paper-panel">
        <p class="eyebrow dark">Juliam Player Wallet</p>
        <h1>Tu wallet</h1>
        <p>Usa tu QR en caja, redime tickets y canjea tus puntos dentro de Juliam Arcade.</p>
      </header>

      <div v-if="message" class="toast" :class="messageType">{{ message }}</div>

      <section class="wallet-card glass-card">
        <div class="balance-block">
          <span>Puntos disponibles</span>
          <strong>{{ formatPoints(account.points_balance || 0) }}</strong>
        </div>
        <div class="qr-card">
          <img v-if="qrImage" :src="qrImage" alt="QR Wallet" />
        </div>
        <p class="earn-note">$10 pesos de compra = 1 punto. 1 punto = $1 peso al canjear.</p>
      </section>

      <section class="action-grid">
        <article class="paper-panel claim-card">
          <p class="eyebrow dark">Tickets</p>
          <h2>Redime tus tickets</h2>
          <p>Escanea el QR impreso en tu ticket.</p>
          <button class="scan-ticket-btn" :disabled="loading" @click="openTicketScanner">Escanear QR</button>
          <small class="scan-note">El QR lo encontraras en tu ticket y sera valido si no fue redimido antes.</small>
        </article>
      </section>

      <section v-if="redemptions.length" class="paper-panel redemptions-panel">
        <div class="section-head compact">
          <div>
            <p class="eyebrow dark">Mis canjes</p>
            <h2>QR generados</h2>
          </div>
        </div>
        <div class="redemption-list">
          <article v-for="redemption in redemptions" :key="redemption.redemption_uuid" class="redemption-row" :class="{ used: !redemption.usable }">
            <div class="redemption-copy">
              <strong>{{ redemptionItemsLabel(redemption) }}</strong>
              <small>{{ formatPoints(redemption.points_spent) }} pts · {{ statusLabel(redemption) }}</small>
            </div>
            <img v-if="redemption.usable && redemption.qr_token" :src="qrFor(redemption.qr_token)" alt="QR de canje" />
            <span v-else class="qr-used">{{ redemption.redeemed_at ? 'Canjeado' : 'No disponible' }}</span>
          </article>
        </div>
      </section>

      <section class="rewards-layout">
        <article class="paper-panel rewards-panel">
          <div class="section-head">
            <div>
              <p class="eyebrow dark">Canjes</p>
              <h2>Arma tu canasta</h2>
              <span>Elige productos, personaliza bebidas y genera un QR unico para caja.</span>
            </div>
            <button class="soft-btn" @click="loadAll">Actualizar</button>
          </div>

          <div class="reward-flags" v-if="rewardGroups.length">
            <button
              v-for="group in rewardGroups"
              :key="group.key"
              type="button"
              :class="{ active: activeRewardGroupKey === group.key }"
              @click="activeRewardGroupKey = group.key"
            >
              {{ group.label }}
            </button>
          </div>

          <div class="rewards-grid">
            <article v-for="reward in activeRewardCards" :key="reward.card_key" class="reward-card" :class="{ disabled: !reward.available }">
              <h3>{{ reward.name }}</h3>
              <div class="reward-meta">
                <strong>{{ formatPoints(pointsPrice(reward, reward._variant)) }} pts</strong>
                <small>{{ stockLabel(reward) }}</small>
              </div>
              <button :disabled="!reward.available || loading || reachedStock(reward)" @click="openRewardCustomizer(reward)">
                {{ !reward.available ? 'No disponible' : (reachedStock(reward) ? 'Stock en canasta' : 'Agregar') }}
              </button>
            </article>
          </div>
          <p v-if="!activeRewardCards.length" class="empty">No hay productos disponibles en esta categoria.</p>
        </article>

        <aside class="basket-panel paper-panel desktop-basket">
          <div class="basket-content">
            <div class="basket-head">
              <div>
                <p class="eyebrow dark">Canasta</p>
                <h2>{{ cartItemCount }} producto{{ cartItemCount === 1 ? '' : 's' }}</h2>
              </div>
              <button class="mini-btn" :disabled="!cartItemCount" @click="clearCart">Limpiar</button>
            </div>
            <div class="basket-list">
              <div v-for="line in cartLines" :key="line.key" class="basket-row">
                <div class="basket-info">
                  <strong>{{ line.display_name }}</strong>
                  <small>{{ formatPoints(line.unit_points) }} pts c/u<span v-if="line.notes"> · {{ line.notes }}</span></small>
                </div>
                <div class="qty-controls">
                  <button @click="decrease(line)">−</button>
                  <span>{{ line.quantity }}</span>
                  <button @click="increase(line)">+</button>
                </div>
                <button class="remove-btn" @click="removeFromCart(line)">Quitar</button>
              </div>
              <div v-if="!cartLines.length" class="empty-cart">
                <strong>Tu canasta esta vacia</strong>
                <small>Agrega recompensas para generar un QR unico.</small>
              </div>
            </div>
            <div class="basket-summary">
              <div><span>Total a canjear</span><strong>{{ formatPoints(cartTotalPoints) }} pts</strong></div>
              <div><span>Saldo despues</span><strong :class="remainingPoints < 0 ? 'danger' : ''">{{ formatPoints(remainingPoints) }} pts</strong></div>
            </div>
            <button class="redeem-btn" :disabled="!canSubmitCart || loading" @click="redeemCart">
              {{ cartTotalPoints <= 0 ? 'Selecciona productos' : (remainingPoints < 0 ? 'Te faltan ' + formatPoints(Math.abs(remainingPoints)) + ' pts' : 'Generar QR de canje') }}
            </button>
            <small class="basket-note">Al confirmar se descuentan tus puntos. Muestra el QR en caja.</small>
          </div>
        </aside>
      </section>

      <section class="paper-panel history-panel">
        <h2>Ultimos movimientos</h2>
        <div class="history-list">
          <div v-for="row in history" :key="row.id" class="history-row">
            <span><b>{{ labelSource(row) }}</b><em v-if="movementReason(row)">{{ movementReason(row) }}</em></span>
            <strong :class="Number(row.points_delta) >= 0 ? 'plus' : 'minus'">{{ Number(row.points_delta) >= 0 ? '+' : '' }}{{ formatPoints(row.points_delta) }} pts</strong>
            <small>{{ row.created_at }}</small>
          </div>
          <div v-if="!history.length" class="empty">Aun no tienes movimientos.</div>
        </div>
      </section>
    </section>

    <section class="mobile-cart-dock" :class="{ open: mobileCartOpen }">
      <button class="dock-summary" type="button" @click="mobileCartOpen = !mobileCartOpen" :aria-expanded="mobileCartOpen ? 'true' : 'false'">
        <span>{{ cartItemCount }} producto{{ cartItemCount === 1 ? '' : 's' }}</span>
        <strong>{{ formatPoints(cartTotalPoints) }} pts</strong>
        <i class="dock-arrow" :class="{ open: mobileCartOpen }" aria-hidden="true">⌃</i>
      </button>
      <div v-if="mobileCartOpen" class="dock-panel">
        <div class="basket-content">
            <div class="basket-head">
              <div>
                <p class="eyebrow dark">Canasta</p>
                <h2>{{ cartItemCount }} producto{{ cartItemCount === 1 ? '' : 's' }}</h2>
              </div>
              <button class="mini-btn" :disabled="!cartItemCount" @click="clearCart">Limpiar</button>
            </div>
            <div class="basket-list">
              <div v-for="line in cartLines" :key="line.key" class="basket-row">
                <div class="basket-info">
                  <strong>{{ line.display_name }}</strong>
                  <small>{{ formatPoints(line.unit_points) }} pts c/u<span v-if="line.notes"> · {{ line.notes }}</span></small>
                </div>
                <div class="qty-controls">
                  <button @click="decrease(line)">−</button>
                  <span>{{ line.quantity }}</span>
                  <button @click="increase(line)">+</button>
                </div>
                <button class="remove-btn" @click="removeFromCart(line)">Quitar</button>
              </div>
              <div v-if="!cartLines.length" class="empty-cart">
                <strong>Tu canasta esta vacia</strong>
                <small>Agrega recompensas para generar un QR unico.</small>
              </div>
            </div>
            <div class="basket-summary">
              <div><span>Total a canjear</span><strong>{{ formatPoints(cartTotalPoints) }} pts</strong></div>
              <div><span>Saldo despues</span><strong :class="remainingPoints < 0 ? 'danger' : ''">{{ formatPoints(remainingPoints) }} pts</strong></div>
            </div>
            <button class="redeem-btn" :disabled="!canSubmitCart || loading" @click="redeemCart">
              {{ cartTotalPoints <= 0 ? 'Selecciona productos' : (remainingPoints < 0 ? 'Te faltan ' + formatPoints(Math.abs(remainingPoints)) + ' pts' : 'Generar QR de canje') }}
            </button>
            <small class="basket-note">Al confirmar se descuentan tus puntos. Muestra el QR en caja.</small>
          </div>
      </div>
    </section>

    <div v-if="showCustomizer" class="scanner-backdrop" @click.self="closeCustomizer">
      <section class="customizer-modal paper-panel">
        <header class="scanner-head">
          <div>
            <p class="eyebrow dark">Personalizar</p>
            <h2>{{ pendingReward?.name }}</h2>
          </div>
          <button class="mini-btn" @click="closeCustomizer">Cerrar</button>
        </header>

        <div v-if="pendingReward" class="customizer-form">
          <div v-if="variantOptions.length" class="option-group">
            <span>Tipo</span>
            <button v-for="option in variantOptions" :key="option.value" type="button" :class="{ active: customForm.variant === option.value }" @click="customForm.variant = option.value">
              {{ option.label }} · {{ formatPoints(pointsPrice(pendingReward, option.value)) }} pts
            </button>
          </div>

          <div v-if="usesMilk(pendingReward)" class="option-group">
            <span>Leche</span>
            <button type="button" :class="{ active: customForm.milk === 'entera' }" @click="customForm.milk = 'entera'">Entera</button>
            <button type="button" :class="{ active: customForm.milk === 'deslactosada' }" @click="customForm.milk = 'deslactosada'">Deslactosada</button>
          </div>

          <label>
            <span class="afro-label">Notas para caja</span>
            <textarea v-model.trim="customForm.notes" class="notes-input" maxlength="180" placeholder="Ej. sin crema, poco hielo, nombre de quien recoge" />
          </label>

          <button class="redeem-btn" type="button" @click="confirmAddToCart">Agregar a canasta</button>
        </div>
      </section>
    </div>

    <div v-if="showTicketScanner" class="scanner-backdrop" @click.self.stop.prevent>
      <section class="scanner-modal paper-panel">
        <header class="scanner-head">
          <div>
            <p class="eyebrow dark">Tickets</p>
            <h2>Escanea el QR</h2>
          </div>
          <button class="mini-btn" @click="closeTicketScanner">Cerrar</button>
        </header>
        <div id="af-ticket-qr-reader" class="af-ticket-qr-reader"></div>
        <p class="scanner-status" :class="scannerError ? 'error' : ''">{{ scannerError || scannerStatus }}</p>
      </section>
    </div>
  </main>
</template>

<script>
import { Html5Qrcode } from 'html5-qrcode'
import { getWallet, getRewards, claimTicket, redeemRewards } from '@/services/menuApi'
import { connectMenuSocket, closeMenuSocket } from '@/services/menuSocket'
import { extractTicketUuid } from '@/utils/ticketCode'

export default {
  name: 'WalletView',
  data() {
    return {
      loading: false,
      account: {}, history: [], rewards: [], redemptions: [], cart: {},
      lastRedemption: null, activeRewardGroupKey: '', mobileCartOpen: false,
      showCustomizer: false, pendingReward: null, customForm: { variant: '', milk: 'entera', notes: '' },
      showTicketScanner: false, ticketScanner: null, scannerStatus: '', scannerError: '',
      message: '', messageType: 'success'
    }
  },
  computed: {
    qrImage() {
      const payload = this.account.wallet_qr_payload || this.account.wallet_token
      return payload ? this.qrFor(payload, 260) : ''
    },
    rewardCards() {
      const cards = []
      this.rewards.forEach((reward) => {
        const layout = reward.category_layout_type || ''
        if (layout === 'beverages_dual') {
          if (Number(reward.has_hot) && reward.price_hot !== null) cards.push({ ...reward, _variant: 'caliente', _groupKey: 'bebidas_calientes', _groupLabel: 'Bebidas calientes', card_key: reward.id_token + ':hot' })
          if (Number(reward.has_cold) && reward.price_cold !== null) cards.push({ ...reward, _variant: 'fria', _groupKey: 'bebidas_frias', _groupLabel: 'Bebidas frias', card_key: reward.id_token + ':cold' })
        } else {
          cards.push({ ...reward, _variant: '', _groupKey: reward.category_slug || reward.category_name || 'juliam', _groupLabel: reward.category_badge_label || reward.category_name || 'Juliam', card_key: reward.id_token + ':single' })
        }
      })
      return cards
    },
    rewardGroups() {
      const map = new Map()
      this.rewardCards.forEach(card => { if (!map.has(card._groupKey)) map.set(card._groupKey, { key: card._groupKey, label: card._groupLabel, items: [] }); map.get(card._groupKey).items.push(card) })
      return Array.from(map.values())
    },
    activeRewardCards() {
      const group = this.rewardGroups.find(row => row.key === this.activeRewardGroupKey) || this.rewardGroups[0]
      return group ? group.items : []
    },
    cartLines() { return Object.values(this.cart) },
    cartItemCount() { return this.cartLines.reduce((sum, line) => sum + Number(line.quantity || 0), 0) },
    cartTotalPoints() { return this.cartLines.reduce((sum, line) => sum + Number(line.unit_points || 0) * Number(line.quantity || 0), 0) },
    remainingPoints() { return Number(this.account.points_balance || 0) - this.cartTotalPoints },
    canSubmitCart() { return this.cartTotalPoints > 0 && this.remainingPoints >= 0 && this.cartLines.every(line => !this.exceedsStock(line)) },
    variantOptions() {
      if (!this.pendingReward) return []
      const out = []
      if (Number(this.pendingReward.has_hot) && this.pendingReward.price_hot !== null) out.push({ value: 'caliente', label: 'Caliente' })
      if (Number(this.pendingReward.has_cold) && this.pendingReward.price_cold !== null) out.push({ value: 'fria', label: 'Fria' })
      return out
    }
  },
  mounted() {
    this.loadAll()
    this._socketOff = connectMenuSocket(this.handleRealtimeUpdate)
  },
  beforeUnmount() {
    this.stopTicketScanner()
    window.clearTimeout(this._realtimeRefreshTimer)
    if (this._socketOff) this._socketOff()
    closeMenuSocket()
  },
  methods: {
    async loadAll() {
      this.loading = true
      try {
        const [wallet, rewards] = await Promise.all([getWallet(), getRewards()])
        this.account = wallet.account || {}
        this.history = wallet.history || []
        this.redemptions = wallet.redemptions || []
        this.rewards = rewards.rewards || []
        if (rewards.points_balance !== undefined) this.account.points_balance = rewards.points_balance
        if (!this.activeRewardGroupKey && this.rewardGroups.length) this.activeRewardGroupKey = this.rewardGroups[0].key
        if (this.activeRewardGroupKey && !this.rewardGroups.some(group => group.key === this.activeRewardGroupKey) && this.rewardGroups.length) this.activeRewardGroupKey = this.rewardGroups[0].key
        this.sanitizeCart()
      } catch (error) { this.show(error.response?.data?.error || 'No se pudo cargar tu wallet.', 'error') }
      finally { this.loading = false }
    },
    qrFor(payload, size = 180) { return `https://api.qrserver.com/v1/create-qr-code/?size=${size}x${size}&data=${encodeURIComponent(payload)}` },
    async openTicketScanner() {
      this.showTicketScanner = true; this.scannerStatus = 'Solicitando permiso de camara...'; this.scannerError = ''
      await this.$nextTick()
      if (!window.isSecureContext) { this.scannerError = 'La cámara requiere HTTPS. Abre Juliam desde https://juliam.gestiontotal.app.'; return }
      try {
        await this.stopTicketScanner(false)
        this.ticketScanner = new Html5Qrcode('af-ticket-qr-reader', false)
        this.scannerStatus = 'Apunta la camara al QR del ticket.'
        await this.ticketScanner.start({ facingMode: 'environment' }, { fps: 10, qrbox: (w, h) => { const edge = Math.min(w, h); const size = Math.max(190, Math.floor(edge * 0.74)); return { width: size, height: size } } }, async (decodedText) => {
          if (!decodedText || !this.showTicketScanner) return
          await this.claimScannedTicket(decodedText)
        }, () => {})
      } catch (error) { this.scannerError = error?.message || 'No se pudo abrir la camara.' }
    },
    async stopTicketScanner(hide = true) { if (this.ticketScanner) { try { await this.ticketScanner.stop() } catch (_e) {} try { await this.ticketScanner.clear() } catch (_e) {} } this.ticketScanner = null; if (hide) this.showTicketScanner = false },
    async closeTicketScanner() { await this.stopTicketScanner(true) },
    async claimScannedTicket(rawValue) {
      const code = extractTicketUuid(rawValue)
      if (!code) { this.show('Este QR no contiene un ticket valido.', 'error'); return }
      this.loading = true; this.scannerStatus = 'Validando ticket...'
      try { await this.stopTicketScanner(true); const data = await claimTicket(code); this.show(`Ticket validado: +${this.formatPoints(data.claim?.points_awarded || 0)} puntos.`, 'success'); await this.loadAll() }
      catch (error) { this.show(error.response?.data?.error || 'No se pudo redimir el ticket.', 'error') }
      finally { this.loading = false }
    },
    handleRealtimeUpdate(payload) {
      if (payload?.event !== 'loyalty.updated') return
      window.clearTimeout(this._realtimeRefreshTimer)
      this._realtimeRefreshTimer = window.setTimeout(async () => {
        try {
          const wallet = await getWallet()
          this.account = wallet.account || this.account
          this.history = wallet.history || []
          this.redemptions = wallet.redemptions || []
        } catch (_error) {
          // La entrega por outbox se reintentara; no interrumpimos al usuario.
        }
      }, 300)
    },
    openRewardCustomizer(reward) {
      if (!reward.available) return
      if (this.reachedStock(reward)) return this.show('Ya agregaste todo el stock disponible de este producto.', 'error')
      this.pendingReward = reward
      this.customForm = { variant: reward._variant || '', milk: this.usesMilk(reward) ? 'entera' : '', notes: '' }
      this.showCustomizer = true
    },
    closeCustomizer() { this.showCustomizer = false; this.pendingReward = null },
    confirmAddToCart() {
      const reward = this.pendingReward; if (!reward) return
      const variant = this.customForm.variant || reward._variant || ''
      const milk = this.usesMilk(reward) ? this.customForm.milk : ''
      const notes = String(this.customForm.notes || '').trim().slice(0, 180)
      const unit = this.pointsPrice(reward, variant)
      const display = this.displayName(reward.name, variant, milk)
      const key = [reward.id_token, variant, milk, notes].join('|')
      const current = this.cart[key]?.quantity || 0
      const line = { key, product_token: reward.id_token, id_token: reward.id_token, name: reward.name, display_name: display, category_name: reward.category_name, variant, milk, notes, quantity: current + 1, unit_points: unit, stock_limit: this.stockLimit(reward), available: reward.available }
      this.cart = { ...this.cart, [key]: line }
      this.closeCustomizer(); this.mobileCartOpen = true
    },
    increase(line) { const nextQty = Number(line.quantity || 0) + 1; if (this.wouldExceedStock(line, nextQty)) return this.show('No hay mas stock disponible de este producto.', 'error'); this.cart = { ...this.cart, [line.key]: { ...line, quantity: nextQty } } },
    decrease(line) { if (line.quantity <= 1) return this.removeFromCart(line); this.cart = { ...this.cart, [line.key]: { ...line, quantity: line.quantity - 1 } } },
    removeFromCart(line) { const next = { ...this.cart }; delete next[line.key]; this.cart = next },
    clearCart() { this.cart = {} },
    async redeemCart() {
      if (!this.canSubmitCart) return this.show('Revisa tu canasta antes de generar el QR.', 'error')
      this.loading = true
      try {
        const items = this.cartLines.map(line => ({ product_token: line.product_token, quantity: line.quantity, variant: line.variant, milk: line.milk, notes: line.notes }))
        const data = await redeemRewards(items)
        this.lastRedemption = data.redemption; this.cart = {}; this.mobileCartOpen = false
        this.show('Canje generado. Muestra el QR en caja.', 'success')
        await this.loadAll()
      } catch (error) { this.show(error.response?.data?.error || 'No se pudo generar el canje.', 'error') }
      finally { this.loading = false }
    },
    pointsPrice(reward, variant = '') {
      const custom = Number(reward.reward_points_price || 0)
      if (custom > 0) return custom
      if (variant === 'fria' && reward.price_cold !== null && reward.price_cold !== undefined && reward.price_cold !== '') return Number(reward.price_cold || 0)
      if (variant === 'caliente' && reward.price_hot !== null && reward.price_hot !== undefined && reward.price_hot !== '') return Number(reward.price_hot || 0)
      if (reward.price !== null && reward.price !== undefined && reward.price !== '') return Number(reward.price || 0)
      return Number(reward.points_price || reward.price_hot || reward.price_cold || 0)
    },
    stockLimit(reward) { const stock = reward.stock_limit ?? reward.reward_stock_quantity; if (stock === null || stock === undefined || stock === '') return null; return Math.max(0, Number(stock)) },
    stockLabel(reward) { const stock = this.stockLimit(reward); return stock === null ? 'Sin limite' : `${stock} disp.` },
    totalInCartForToken(token) { return this.cartLines.filter(line => line.product_token === token).reduce((sum, line) => sum + Number(line.quantity || 0), 0) },
    reachedStock(reward) { const stock = this.stockLimit(reward); return stock !== null && this.totalInCartForToken(reward.id_token || reward.product_token) >= stock },
    wouldExceedStock(line, nextQty) { const stock = this.stockLimit(line); if (stock === null) return false; const currentOther = this.totalInCartForToken(line.product_token || line.id_token) - Number(line.quantity || 0); return currentOther + Number(nextQty || 0) > stock },
    exceedsStock(line) { const stock = this.stockLimit(line); return stock !== null && this.totalInCartForToken(line.product_token || line.id_token) > stock },
    sanitizeCart() { this.cart = {} },
    usesMilk(reward) { return ['bebidas_calientes', 'bebidas_frias', 'frappe', 'especiales-frias', 'bebidas'].some(key => String(reward._groupKey || reward.category_slug || '').includes(key)) || Number(reward.has_hot) || Number(reward.has_cold) },
    displayName(name, variant, milk) { const parts = []; if (variant) parts.push(variant); if (milk) parts.push(`leche ${milk}`); return parts.length ? `${name} (${parts.join(', ')})` : name },
    redemptionItemsLabel(redemption) { const items = redemption?.items || []; if (!items.length) return 'Canje generado'; return items.map(item => `${Number(item.quantity || 1)}x ${item.product_name || item.name}`).join(' · ') },
    statusLabel(redemption) { if (redemption.usable) return 'Listo para usar'; if (redemption.redeemed_at) return `Canjeado ${redemption.redeemed_at}`; return redemption.status || 'No disponible' },
    metadata(row) { if (!row) return {}; if (typeof row.metadata === 'object' && row.metadata) return row.metadata; try { return JSON.parse(row.metadata || '{}') || {} } catch (_) { return {} } },
    labelSource(row) { const source = typeof row === 'string' ? row : row?.source_type; const meta = typeof row === 'string' ? {} : this.metadata(row); if (meta.label) return meta.label; return { ticket_claim: 'Ticket redimido', reward_redeem: 'Canje de recompensa', sync_adjust: 'Sincronizacion', pos_award: 'Compra en tienda', pos_sale: 'Compra en tienda', manual_adjust: 'Puntos asignados por el negocio', business_adjust: 'Puntos asignados por el negocio' }[source] || source || 'Movimiento de puntos' },
    movementReason(row) { const meta = this.metadata(row); return meta.reason || meta.ticket_number || '' },
    formatPoints(value) { const number = Number(value || 0); return Number.isInteger(number) ? String(number) : number.toFixed(2).replace(/0+$/, '').replace(/\.$/, '') },
    show(text, type = 'success') { this.message = text; this.messageType = type; window.clearTimeout(this._toast); this._toast = window.setTimeout(() => { this.message = '' }, 5200) }
  }
}
</script>

<style scoped>
.wallet-page { min-height: 100vh; padding: 92px 28px 96px; color: var(--afro-ink); }
.wallet-shell { width: min(1320px, 100%); margin: 0 auto; display: grid; gap: 18px; }
.paper-panel { border-radius: 28px; background: rgba(243,223,189,.94); border: 1px solid rgba(38,51,31,.16); padding: 22px; box-shadow: 0 16px 44px rgba(0,0,0,.12); }
.wallet-info { text-align: left; overflow: hidden; }
.eyebrow { margin: 0 0 8px; color: var(--afro-gold); font-weight: 1000; text-transform: uppercase; letter-spacing: .16em; }
.eyebrow.dark { color: #8a5a0c; }
h1, h2, h3 { font-family: var(--afro-text-serif); }
h1 { margin: 0; font-size: clamp(42px, 7vw, 74px); color: var(--afro-ink); }
h2 { margin: 0 0 8px; }
.wallet-info p:last-child { margin: 8px 0 0; color: var(--afro-olive); font-weight: 800; }
.wallet-card { border-radius: 32px; padding: 24px; display: grid; grid-template-columns: 1fr minmax(220px, 310px); gap: 18px; align-items: center; background: linear-gradient(135deg, rgba(214,163,75,.92), rgba(243,223,189,.96)); box-shadow: 0 22px 56px rgba(0,0,0,.18); }
.balance-block span { display: block; text-transform: uppercase; letter-spacing: .12em; font-weight: 1000; color: rgba(38,51,31,.72); }
.balance-block strong { display: block; font-size: clamp(76px, 18vw, 150px); line-height: .9; font-weight: 1000; }
.qr-card { border-radius: 28px; background: rgba(255,248,230,.82); padding: 18px; display: grid; place-items: center; text-align: center; }
.qr-card img { width: 220px; height: 220px; border-radius: 18px; }
.earn-note { grid-column: 1 / -1; margin: 0; text-align: center; color: rgba(38,51,31,.75); font-weight: 1000; }
.action-grid { display: grid; grid-template-columns: 1fr; gap: 18px; }
.scan-ticket-btn { width: 100%; margin-top: 16px; min-height: 54px; background: linear-gradient(135deg, #26331f, #4a5d2c); }
.scan-note { display: block; margin-top: 10px; color: rgba(38,51,31,.65); font-weight: 800; }
.section-head, .basket-head { display: flex; justify-content: space-between; gap: 16px; align-items: flex-start; margin-bottom: 18px; }
.section-head.compact { margin-bottom: 12px; }
.section-head h2 { font-size: clamp(34px, 5vw, 54px); }
.section-head span { color: rgba(38,51,31,.72); font-weight: 800; }
.rewards-layout { display: grid; grid-template-columns: minmax(0, 1fr) 390px; gap: 18px; align-items: start; }
.reward-flags { display: flex; flex-wrap: wrap; gap: 9px; margin-bottom: 16px; }
.reward-flags button { background: rgba(255,248,230,.58); color: var(--afro-olive); border: 1px solid rgba(38,51,31,.16); border-radius: 999px; padding: 10px 13px; font-size: 12px; }
.reward-flags button.active { background: #26331f; color: #f3dfbd; }
.rewards-grid { display: grid; grid-template-columns: repeat(2, minmax(0, 1fr)); gap: 12px; }
.reward-card { border: 1px solid rgba(38,51,31,.16); border-radius: 20px; padding: 14px; background: rgba(255,248,230,.56); display: grid; gap: 10px; min-height: 160px; }
.reward-card.disabled { filter: grayscale(.3); opacity: .72; }
.reward-card h3 { margin: 0; font-size: clamp(20px, 3vw, 25px); line-height: 1.05; }
.reward-meta { display: flex; justify-content: space-between; gap: 8px; align-items: center; }
.reward-meta strong { font-size: 22px; color: #8a5a0c; }
.reward-meta small { color: var(--afro-olive); font-weight: 900; }
.basket-panel { position: sticky; top: 82px; }
.basket-list { display: grid; gap: 10px; max-height: 440px; overflow: auto; padding-right: 2px; }
.basket-row { border: 1px solid rgba(38,51,31,.14); border-radius: 20px; background: rgba(255,248,230,.56); padding: 12px; display: grid; gap: 10px; }
.basket-info strong { display: block; }
.basket-info small { color: rgba(38,51,31,.62); font-weight: 800; }
.qty-controls { display: grid; grid-template-columns: 42px 1fr 42px; gap: 8px; align-items: center; }
.qty-controls button { padding: 0; height: 38px; border-radius: 14px; }
.qty-controls span { height: 38px; border-radius: 14px; background: rgba(38,51,31,.09); display: grid; place-items: center; font-weight: 1000; }
.empty-cart { border: 1px dashed rgba(38,51,31,.22); border-radius: 20px; padding: 22px; display: grid; gap: 6px; text-align: center; color: rgba(38,51,31,.66); }
.basket-summary { margin: 16px 0; border-radius: 22px; background: rgba(38,51,31,.08); padding: 14px; display: grid; gap: 10px; }
.basket-summary div { display: flex; justify-content: space-between; gap: 12px; font-weight: 900; }
.basket-summary .danger { color: #991b1b; }
.redemption-list { display: grid; gap: 10px; }
.redemption-row { display: grid; grid-template-columns: 1fr 92px; align-items: center; gap: 14px; border: 1px solid rgba(38,51,31,.14); border-radius: 20px; padding: 12px; background: rgba(255,248,230,.54); }
.redemption-row.used { opacity: .68; }
.redemption-row img { width: 84px; height: 84px; background: #fff; border-radius: 12px; padding: 6px; }
.redemption-copy strong { display: block; }
.redemption-copy small { color: rgba(38,51,31,.62); font-weight: 800; }
.qr-used { border-radius: 14px; background: rgba(38,51,31,.09); min-height: 48px; display: grid; place-items: center; font-weight: 900; color: var(--afro-olive); }
.history-list { display: grid; gap: 8px; }
.history-row { display: grid; grid-template-columns: 1fr auto auto; gap: 12px; align-items: center; border: 1px solid rgba(38,51,31,.12); background: rgba(255,248,230,.5); border-radius: 16px; padding: 12px 14px; }
.history-row span { font-weight: 900; display: grid; gap: 2px; }
.history-row span em { font-style: normal; font-size: 12px; color: rgba(38,51,31,.62); font-weight: 800; }
.history-row .plus { color: #166534; } .history-row .minus { color: #991b1b; } .history-row small { color: rgba(38,51,31,.6); }
button { border: 0; border-radius: 18px; padding: 13px 18px; background: #26331f; color: var(--afro-paper-light); font-weight: 1000; cursor: pointer; transition: transform .16s ease, opacity .16s ease; }
button:hover:not(:disabled) { transform: translateY(-1px); } button:disabled { opacity: .5; cursor: not-allowed; }
.soft-btn, .mini-btn { background: rgba(38,51,31,.1); color: #26331f; }
.mini-btn { padding: 10px 14px; }
.remove-btn { background: transparent; color: #8a5a0c; border: 1px solid rgba(138,90,12,.25); padding: 9px 12px; }
.redeem-btn { width: 100%; min-height: 54px; background: linear-gradient(135deg, #26331f, #4a5d2c); }
.basket-note { display: block; margin-top: 10px; color: rgba(38,51,31,.62); font-weight: 800; text-align: center; }
.empty { color: rgba(38,51,31,.6); font-weight: 900; }
.toast { border-radius: 18px; padding: 14px 16px; font-weight: 1000; } .toast.success { background: #dcfce7; color: #166534; } .toast.error { background: #fee2e2; color: #991b1b; }
.scanner-backdrop { position: fixed; inset: 0; background: rgba(8,12,7,.72); z-index: 80; display: grid; place-items: center; padding: 18px; }
.scanner-modal, .customizer-modal { width: min(560px, 100%); background: rgba(243,223,189,.98); }
.scanner-head { display: flex; justify-content: space-between; align-items: flex-start; gap: 14px; margin-bottom: 14px; }
.af-ticket-qr-reader { min-height: 320px; border-radius: 24px; overflow: hidden; background: #05070b; }
.af-ticket-qr-reader :deep(video) { width: 100% !important; min-height: 320px; object-fit: cover; }
.af-ticket-qr-reader :deep(img), .af-ticket-qr-reader :deep(span), .af-ticket-qr-reader :deep(div[style*="display: none"]) { display: none !important; }
.scanner-status { margin: 12px 0 0; color: rgba(38,51,31,.72); font-weight: 900; } .scanner-status.error { color: #991b1b; }
.customizer-form { display: grid; gap: 14px; }
.option-group { display: flex; flex-wrap: wrap; gap: 8px; align-items: center; }
.option-group span { width: 100%; font-weight: 1000; color: var(--afro-olive); }
.option-group button { background: rgba(255,248,230,.62); color: var(--afro-olive); border: 1px solid rgba(38,51,31,.16); }
.option-group button.active { background: #26331f; color: #f3dfbd; }
.notes-input { width: 100%; min-height: 92px; border: 1px solid rgba(38,51,31,.22); border-radius: 18px; padding: 14px; background: rgba(255,248,230,.78); outline: none; font-weight: 800; resize: vertical; }
.mobile-cart-dock { display: none; }
@media (max-width: 1120px) { .rewards-layout { grid-template-columns: 1fr; } .basket-panel { position: static; } }
@media (max-width: 760px) {
  .wallet-page { padding: 78px 0 104px; } .wallet-shell { gap: 14px; } .wallet-info, .wallet-card { border-radius: 0 0 26px 26px; } .wallet-info { padding-top: 22px; }
  .wallet-card { grid-template-columns: 1fr; text-align: center; } .qr-card { width: min(280px, 100%); margin: 0 auto; } .qr-card img { width: 210px; height: 210px; }
  .action-grid, .rewards-layout, .history-panel, .redemptions-panel { padding-left: 12px; padding-right: 12px; } .paper-panel { border-radius: 24px; }
  .rewards-grid { grid-template-columns: repeat(2, minmax(0, 1fr)); gap: 10px; } .reward-card { padding: 12px; min-height: 146px; } .reward-card h3 { font-size: 18px; } .reward-meta { display: grid; gap: 2px; } .reward-meta strong { font-size: 19px; }
  .desktop-basket { display: none; } .mobile-cart-dock { position: fixed; left: 0; right: 0; bottom: 0; z-index: 65; display: block; padding: 10px; background: linear-gradient(180deg, rgba(18,23,14,.15), rgba(18,23,14,.96) 28%); }
  .dock-summary { width: 100%; min-height: 56px; border-radius: 999px; display: grid; grid-template-columns: 1fr auto 38px; gap: 10px; justify-content: space-between; align-items: center; padding: 0 10px 0 18px; box-shadow: 0 16px 38px rgba(0,0,0,.32); }
  .dock-arrow { width: 32px; height: 32px; border-radius: 999px; display: grid; place-items: center; background: rgba(255,255,255,.18); font-style: normal; font-size: 24px; line-height: 1; transition: transform .2s ease; }
  .dock-arrow.open { transform: rotate(180deg); }
  .dock-panel { max-height: 70vh; overflow: auto; margin-bottom: 10px; border-radius: 24px; background: rgba(243,223,189,.98); padding: 14px; box-shadow: 0 -14px 36px rgba(0,0,0,.28); }
  .history-row { grid-template-columns: 1fr auto; } .history-row small { grid-column: 1 / -1; } .redemption-row { grid-template-columns: 1fr 80px; } .redemption-row img { width: 72px; height: 72px; }
}

/* Línea gráfica Juliam Arcade */
.wallet-page { color: var(--juliam-text); }
.paper-panel {
  color: var(--juliam-text);
  background: linear-gradient(145deg, rgba(17, 24, 45, .96), rgba(7, 11, 25, .97));
  border: 1px solid rgba(255, 255, 255, .12);
  border-radius: 18px;
  box-shadow: 0 22px 58px rgba(0, 0, 0, .34), inset 0 1px 0 rgba(255, 255, 255, .035);
}
.wallet-info { border-top: 2px solid var(--juliam-pink); }
.eyebrow, .eyebrow.dark { color: var(--juliam-cyan); font: 900 10px/1 var(--juliam-mono); }
h1, h2, h3 { color: var(--juliam-text); font-family: var(--juliam-sans); }
h1 { color: var(--juliam-text); letter-spacing: -.055em; text-transform: uppercase; }
.wallet-info p:last-child, .section-head span { color: var(--juliam-muted); font-weight: 600; }
.wallet-card {
  border: 1px solid rgba(76, 245, 255, .38);
  border-radius: 20px;
  background:
    radial-gradient(circle at 82% 24%, rgba(255, 63, 216, .2), transparent 31%),
    linear-gradient(135deg, rgba(14, 28, 56, .98), rgba(8, 11, 28, .98));
  box-shadow: 0 0 36px rgba(76, 245, 255, .08), 0 24px 54px rgba(0, 0, 0, .34);
}
.balance-block span { color: var(--juliam-muted); font: 900 10px/1 var(--juliam-mono); }
.balance-block strong { color: var(--juliam-cyan); font-family: var(--juliam-mono); text-shadow: 0 0 24px rgba(76, 245, 255, .34); }
.qr-card { border: 1px solid rgba(255, 63, 216, .35); border-radius: 16px; background: #fff; box-shadow: 0 0 25px rgba(255, 63, 216, .12); }
.qr-card img { border-radius: 8px; }
.earn-note, .scan-note, .basket-note, .empty, .scanner-status { color: var(--juliam-muted); }
.scan-ticket-btn, .redeem-btn { border: 1px solid rgba(255, 63, 216, .55); border-radius: 10px; background: rgba(255, 63, 216, .11); color: var(--juliam-pink); box-shadow: 0 0 20px rgba(255, 63, 216, .08); font: 900 11px/1 var(--juliam-mono); letter-spacing: .08em; text-transform: uppercase; }
button { border: 1px solid rgba(76, 245, 255, .34); border-radius: 9px; background: rgba(76, 245, 255, .08); color: var(--juliam-cyan); font: 800 11px/1.2 var(--juliam-mono); }
.soft-btn, .mini-btn { background: rgba(255, 255, 255, .035); color: var(--juliam-muted); border-color: rgba(255, 255, 255, .15); }
.reward-flags button, .option-group button { border-color: rgba(255, 255, 255, .13); background: rgba(255, 255, 255, .035); color: var(--juliam-muted); }
.reward-flags button.active, .option-group button.active { border-color: rgba(76, 245, 255, .55); background: rgba(76, 245, 255, .1); color: var(--juliam-cyan); }
.reward-card, .basket-row, .redemption-row, .history-row {
  border-color: rgba(255, 255, 255, .1);
  border-radius: 12px;
  background: rgba(5, 8, 19, .56);
}
.reward-meta strong, .history-row .plus { color: var(--juliam-lime); }
.reward-meta small, .basket-info small, .redemption-copy small, .history-row span em, .history-row small { color: var(--juliam-muted); }
.qty-controls span, .basket-summary, .qr-used { background: rgba(255, 255, 255, .045); color: var(--juliam-text); }
.empty-cart { border-color: rgba(255, 255, 255, .15); color: var(--juliam-muted); }
.basket-summary .danger, .history-row .minus, .scanner-status.error { color: var(--juliam-pink); }
.remove-btn { border-color: rgba(255, 63, 216, .28); background: transparent; color: var(--juliam-pink); }
.toast { border-radius: 10px; border: 1px solid; }
.toast.success { border-color: rgba(167, 255, 79, .35); background: rgba(167, 255, 79, .09); color: var(--juliam-lime); }
.toast.error { border-color: rgba(255, 63, 216, .35); background: rgba(255, 63, 216, .09); color: var(--juliam-pink); }
.scanner-backdrop { background: rgba(1, 3, 10, .82); backdrop-filter: blur(8px); }
.scanner-modal, .customizer-modal, .dock-panel { background: linear-gradient(145deg, #11182d, #070b19); }
.notes-input { border-color: rgba(255, 255, 255, .15); border-radius: 10px; background: rgba(5, 8, 19, .7); color: var(--juliam-text); }
.mobile-cart-dock { background: linear-gradient(180deg, rgba(5, 8, 19, 0), rgba(5, 8, 19, .98) 28%); }

@media (max-width: 760px) {
  .wallet-info, .wallet-card { border-radius: 0 0 20px 20px; }
  .paper-panel, .dock-panel { border-radius: 16px; }
}
</style>
